import { Ajax } from '../ajax/Ajax';
import { Auth } from '../auth/Auth';
import { SDK } from '../index';
import { IDoryConfig } from '../interface/IDoryConfig';

// #region Enums
enum UserDetailsAction {
  set = 'set',
  add = 'add',
  remove = 'remove',
}
// #endregion

/**
 * These functions require admin privileges.
 */
export class Admin extends Ajax {
  /**
   * @hidden
   */
  static install(instance: SDK) {
    instance['_admin'] = new Admin(instance.config, instance.Auth);
  }

  private config: IDoryConfig;
  /**
   * @hidden
   */
  constructor(config: IDoryConfig, serviceAuth: Auth) {
    super(config.admin);
    this.auth = serviceAuth;
    this.config = config;
  }
  /**
   * Set admin Endpoint
   *
   * @param newEndpoint  full url to new endpoint
   */
  public setUrlEndpoint(newEndpoint: string): void {
    this.config.admin.url = newEndpoint;
  }
  /**
   * Get admin endpoint
   *
   * @returns URL of the Endpoint
   */
  public getUrlEndpoint(): string {
    return this.config.admin.url;
  }
  /**
   * Lists all active sessions for a product
   *
   * @param product  ProductName
   */
  public async getSessions(product: string) {
    return this.get(`/sessions?product=${product}`);
  }
  /**
   *  Gets the Login information for a user
   *
   * @param product  Productname
   * @param loginname  User loginname
   */
  public async getUserLoginInfo(product: string, loginname: string) {
    return this.get(`/users?product=${product}&loginname=${loginname}`);
  }
  /**
   * Gets the list of user details
   *
   * @param entity  Entity
   * @param path  UserDetails path
   * @param includeSource  Include source
   *
   * @returns The details of the requested user
   */
  public async getUserDetails(
    entity: string,
    path: string,
    includeSource = false
  ) {
    return this.get(
      `/entities/${entity}/userdetails?path=${path}&includeSource=${includeSource}`
    );
  }
  /**
   *
   * @param entity  Entity
   * @param path  UserDetails path
   * @param object  Object
   * @param profile  Profile
   * @param action  Action
   *
   * @returns The response of the server
   */
  public async setUserDetails(
    entity: string,
    path: string,
    object: string,
    profile: string,
    action = UserDetailsAction.set
  ) {
    return this.post(`/entities/${entity}/userdetails`, {
      path,
      object,
      profile,
      action,
    });
  }
  /**
   * Copy a specific user
   *
   * @param copyfrom  Copy Request object
   * @param loginname  User login name
   * @param productname Product name
   * @param validdays  Set valid for days, 0 = unlimited
   * @param firstTimeUser  Set first time user flag
   *
   * @returns The response of the server
   */
  public async copyEntity(
    copyfrom: string,
    loginname: string,
    productname: string,
    validdays = 0,
    firstTimeUser: true
  ) {
    return this.post(`/entities/copy`, {
      copyfrom,
      loginname,
      productname,
      validdays,
      firstTimeUser,
    });
  }
  /**
   * Set password for user
   *
   * @param loginname  User login name
   * @param product  Product name
   * @param password  New password
   * @param temporary  Set temporary password flag
   *
   * @returns The response of the server
   */
  public async setUserPassword(
    loginname: string,
    product: string,
    password: string,
    temporary = false
  ) {
    return this.put(`/entities/logins/newpwd`, {
      loginname,
      product,
      password,
      temporary,
    });
  }
  /**
   * Sets the validity in days of the login (ValidUntil). Activates the user and resets the locks.
   *
   * @param loginname  User login name
   * @param product  Product name
   * @param validdays  Set valid for days, 0 = unlimited
   *
   * @returns The response of the server
   */
  public async setUserLoginValidityDays(
    loginname: string,
    product: string,
    validdays = 0
  ) {
    return this.put(`/entities/logins/setvaliditydays`, {
      loginname,
      product,
      validdays,
    });
  }
  /**
   * Sets the userlogin active/inactive (active). Activates the user and resets the locks.
   *
   * @param loginname  User login name
   * @param product  Product name
   * @param active  IsActive flag
   *
   * @returns The response of the server
   */
  public async setUserIsActive(
    loginname: string,
    product: string,
    active = true
  ) {
    return this.put(`/entities/logins/setisactive`, {
      loginname,
      product,
      active,
    });
  }
}
